import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useRecaptchaV3 from "./hooks/reCaptchaV3";
import {
  getRecaptchaSiteKey,
  RECAPTCHA_ACTIONS,
} from "../../config/recaptchaConfig";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Progress,
} from "reactstrap";
import InputsValidations from "../InputsValidations.js";
import ContactModal from "./components/ContactModal";
import AddressModal from "./components/AddressModal";
import BusinessAddressModal from "./components/BusinessAddressModal";
import ConfirmationModal from "components/ConfirmationModal";
import ExtAddressAutocomplete from "components/extAddressAutocomplete";
import merchantExternalController from "../../service/External/MerchantExternalController.ts";
import applicationExternalController from "../../service/External/ApplicationExternalController.ts";
import communicationExternalController from "../../service/External/CommunicationExternalController.ts";
import templateExternalController from "../../service/External/TemplateExternalController.ts";

// Import step components
import ExternalAmountStep from "./steps/ExternalAmountStep";
import ExternalOwnerInformation from "./steps/ExternalOwnerInformation";
import ExternalBusinessInformation from "./steps/ExternalBusinessInformation";
import ExternalBankInformation from "./steps/ExternalBankInformation";

const STEPS = {
  AMOUNT: 0,
  OWNER: 1,
  BUSINESS: 2,
  BANK: 3,
};

const ExternalApplicationWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(STEPS.AMOUNT);

  // Initialize reCAPTCHA v3 hook
  const executeRecaptcha = useRecaptchaV3(getRecaptchaSiteKey());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    // Step 1: Business Metrics + Monitoring Center
    requestedAmount: "",
    monitoringCenter: "",
    monitoringCenterId: null,

    // Step 2: Owner Information (current owner being edited)
    currentOwner: {
      firstName: "",
      middleName: "",
      lastName: "",
      gender: "MALE",
      birthDate: "",
      citizenship: "",
      ownershipPercentage: "",
      ssn: "",
      contacts: [],
      addresses: [],
    },

    // All owners (array of owners)
    owners: [],

    // Step 3: Business Information
    totalSubscribers: "",
    totalMonthlyRevenue: "",
    legalName: "",
    dbaName: "",
    federalTaxId: "",
    website: "",
    stateOfInc: "",
    industry: "Alarms",
    businessStructure: "",
    dateEstablished: "",
    businessPhoneNumber: "",

    // Business addresses (multiple addresses with phoneNumber)
    businessAddresses: [],

    // Step 4: Bank Information (current bank being edited)
    currentBank: {
      type: "ACH",
      bankName: "",
      accountName: "",
      accountNumber: "",
      routingNumber: "",
      bankAddress: "",
      bankCity: "",
      bankState: "",
      bankCountry: "USA",
    },

    // All banks (array of banks)
    banks: [],
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Modal states for contacts and addresses
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showBusinessAddressModal, setShowBusinessAddressModal] =
    useState(false);
  const [contactToEdit, setContactToEdit] = useState(null);
  const [addressToEdit, setAddressToEdit] = useState(null);
  const [businessAddressToEdit, setBusinessAddressToEdit] = useState(null);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmType, setConfirmType] = useState("delete"); // 'delete' or 'edit'

  // Editing states
  const [editingOwnerIndex, setEditingOwnerIndex] = useState(null);
  const [editingBankIndex, setEditingBankIndex] = useState(null);

  // Options for dropdowns
  const [monitoringCenterOptions, setMonitoringCenterOptions] = useState([]);

  // Load dropdown options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const monitoringCentersExternal = await merchantExternalController
          .getMonitoringCenterLegalNames()
          .catch(() => []);
        console.log(
          "Monitoring Centers from external endpoint: ",
          monitoringCentersExternal
        );
        // Handle both array response and paginated response
        const monitoringCenters = Array.isArray(monitoringCentersExternal)
          ? monitoringCentersExternal
          : monitoringCentersExternal?.data || [];
        console.log("Monitoring centers: ", monitoringCenters);
        const monitoringOptions = monitoringCenters.map((mc) => {
          // Handle the new structure with id and legal_name
          if (typeof mc === "object" && mc !== null) {
            const id = mc.id;
            const name =
              mc.legal_name || mc["legal-name"] || mc.legalName || mc.name;
            return {
              value: name,
              label: name,
              id: id,
            };
          }
          // Fallback for old string format
          return {
            value: mc,
            label: mc,
            id: null,
          };
        });
        setMonitoringCenterOptions(monitoringOptions);

        // Set default monitoring center if available
        if (monitoringOptions.length > 0) {
          setFormData((prev) => ({
            ...prev,
            monitoringCenter: monitoringOptions[0].value,
            monitoringCenterId: monitoringOptions[0].id,
          }));
        }
      } catch (e) {
        console.error("Error loading options:", e);
      }
    };
    loadOptions();
  }, []);

  // Handle input changes with validation
  const handleInputChange = (
    field,
    value,
    isBankField = false,
    isOwnerField = false
  ) => {
    let processedValue = value;

    // Apply formatting for specific fields
    if (field === "federalTaxId") {
      processedValue = InputsValidations.formatFederalTaxId(
        value,
        formData.federalTaxId
      );
    } else if (field === "ssn") {
      // Format SSN automatically
      const digits = value.replace(/\D/g, "");
      if (digits.length <= 3) {
        processedValue = digits;
      } else if (digits.length <= 5) {
        processedValue = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        processedValue = `${digits.slice(0, 3)}-${digits.slice(
          3,
          5
        )}-${digits.slice(5, 9)}`;
      }
    } else if (field === "requestedAmount" || field === "totalSubscribers") {
      // Only digits
      processedValue = value.replace(/\D/g, "");
      if (processedValue.length > 10)
        processedValue = processedValue.slice(0, 10);
    } else if (field === "totalMonthlyRevenue") {
      // Allow digits and one decimal point
      processedValue = value.replace(/[^\d.]/g, "");
      const parts = processedValue.split(".");
      if (parts.length > 2) {
        processedValue = parts[0] + "." + parts.slice(1).join("");
      }
      if (parts[1] && parts[1].length > 2) {
        processedValue = parts[0] + "." + parts[1].slice(0, 2);
      }
    } else if (field === "ownershipPercentage") {
      // Allow decimals for ownership percentage (max 100%)
      // Remove any non-numeric characters except decimal point
      let cleanValue = value.replace(/[^\d.]/g, "");

      // Ensure only one decimal point
      const parts = cleanValue.split(".");
      if (parts.length > 2) {
        cleanValue = parts[0] + "." + parts.slice(1).join("");
      }

      // Limit to 2 decimal places
      if (parts[1] && parts[1].length > 2) {
        cleanValue = parts[0] + "." + parts[1].slice(0, 2);
      }

      // Validate max 100%
      const num = parseFloat(cleanValue);
      if (!isNaN(num) && num > 100) {
        processedValue = "100";
      } else {
        processedValue = cleanValue;
      }
    } else if (field === "citizenship") {
      // Limit citizenship to 10 characters
      processedValue = value.slice(0, 10).toUpperCase();
    }

    // Update form data
    if (isBankField) {
      setFormData((prev) => ({
        ...prev,
        currentBank: {
          ...prev.currentBank,
          [field]: processedValue,
        },
      }));
    } else if (isOwnerField) {
      setFormData((prev) => ({
        ...prev,
        currentOwner: {
          ...prev.currentOwner,
          [field]: processedValue,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: processedValue,
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate website format
  const validateWebsiteField = (value) => {
    if (!value || value.trim() === "") {
      return null; // Website is optional
    }
    const validation = InputsValidations.validateWebsite(value);
    return validation.isValid ? null : validation.message;
  };

  // Handle contact management for current owner
  const handleAddContact = (contact) => {
    const rawType = (contact.type || "").toString().trim().toUpperCase();
    const contactType = rawType === "PHONE" ? "Phone" : "Email";
    const isPrimary = contact.isPrimary ? "Yes" : "No";

    if (isPrimary === "Yes") {
      const alreadyHasPrimaryOfType = formData.currentOwner.contacts.some(
        (c) => c.type === contactType && c.primary === "Yes"
      );
      if (alreadyHasPrimaryOfType) {
        alert(
          `A primary ${contactType} already exists. Another one cannot be assigned.`
        );
        return;
      }
    }

    const newContact = {
      ...contact,
      id: Date.now(),
      type: contactType,
      primary: isPrimary,
    };

    setFormData((prev) => ({
      ...prev,
      currentOwner: {
        ...prev.currentOwner,
        contacts: [...prev.currentOwner.contacts, newContact],
      },
    }));
    setShowContactModal(false);
  };

  const handleEditContact = (contact) => {
    setContactToEdit(contact);
    setShowContactModal(true);
  };

  const handleUpdateContact = (updatedContact) => {
    const rawType = (updatedContact.type || "").toString().trim().toUpperCase();
    const contactType = rawType === "PHONE" ? "Phone" : "Email";
    const isPrimary = updatedContact.isPrimary ? "Yes" : "No";

    if (isPrimary === "Yes") {
      const alreadyHasPrimaryOfType = formData.currentOwner.contacts.some(
        (c) =>
          c.id !== contactToEdit.id &&
          c.type === contactType &&
          c.primary === "Yes"
      );
      if (alreadyHasPrimaryOfType) {
        alert(
          `A primary ${contactType} already exists. Another one cannot be assigned.`
        );
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      currentOwner: {
        ...prev.currentOwner,
        contacts: prev.currentOwner.contacts.map((c) =>
          c.id === contactToEdit.id
            ? {
                ...updatedContact,
                id: contactToEdit.id,
                type: contactType, // <-- normalizado
                primary: isPrimary,
              }
            : c
        ),
      },
    }));
    setShowContactModal(false);
    setContactToEdit(null);
  };

  const confirmDeleteContact = (contactId) => {
    setConfirmAction(() => () => {
      setFormData((prev) => ({
        ...prev,
        currentOwner: {
          ...prev.currentOwner,
          contacts: prev.currentOwner.contacts.filter(
            (c) => c.id !== contactId
          ),
        },
      }));
      setShowConfirmModal(false);
    });
    setConfirmType("delete");
    setShowConfirmModal(true);
  };

  const handleDeleteContact = (contactId) => {
    confirmDeleteContact(contactId);
  };

  // Handle address management for current owner
  const handleAddAddress = (address) => {
    const newAddress = {
      ...address,
      id: Date.now(),
    };
    setFormData((prev) => ({
      ...prev,
      currentOwner: {
        ...prev.currentOwner,
        addresses: [...prev.currentOwner.addresses, newAddress],
      },
    }));
    setShowAddressModal(false);
  };

  const handleEditAddress = (address) => {
    setAddressToEdit(address);
    setShowAddressModal(true);
  };

  const handleUpdateAddress = (updatedAddress) => {
    setFormData((prev) => ({
      ...prev,
      currentOwner: {
        ...prev.currentOwner,
        addresses: prev.currentOwner.addresses.map((a) =>
          a.id === addressToEdit.id
            ? { ...updatedAddress, id: addressToEdit.id }
            : a
        ),
      },
    }));
    setShowAddressModal(false);
    setAddressToEdit(null);
  };

  const confirmDeleteAddress = (addressId) => {
    setConfirmAction(() => () => {
      setFormData((prev) => ({
        ...prev,
        currentOwner: {
          ...prev.currentOwner,
          addresses: prev.currentOwner.addresses.filter(
            (a) => a.id !== addressId
          ),
        },
      }));
      setShowConfirmModal(false);
    });
    setConfirmType("delete");
    setShowConfirmModal(true);
  };

  const handleDeleteAddress = (addressId) => {
    confirmDeleteAddress(addressId);
  };

  // Handle business address management
  const handleAddBusinessAddress = (address) => {
    const newAddress = {
      ...address,
      id: Date.now(),
    };
    setFormData((prev) => ({
      ...prev,
      businessAddresses: [...prev.businessAddresses, newAddress],
    }));
    setShowBusinessAddressModal(false);
  };

  const handleEditBusinessAddress = (address) => {
    setBusinessAddressToEdit(address);
    setShowBusinessAddressModal(true);
  };

  const handleUpdateBusinessAddress = (updatedAddress) => {
    setFormData((prev) => ({
      ...prev,
      businessAddresses: prev.businessAddresses.map((a) =>
        a.id === businessAddressToEdit.id
          ? { ...updatedAddress, id: businessAddressToEdit.id }
          : a
      ),
    }));
    setShowBusinessAddressModal(false);
    setBusinessAddressToEdit(null);
  };

  const confirmDeleteBusinessAddress = (addressId) => {
    setConfirmAction(() => () => {
      setFormData((prev) => ({
        ...prev,
        businessAddresses: prev.businessAddresses.filter(
          (a) => a.id !== addressId
        ),
      }));
      setShowConfirmModal(false);
    });
    setConfirmType("delete");
    setShowConfirmModal(true);
  };

  const handleDeleteBusinessAddress = (addressId) => {
    confirmDeleteBusinessAddress(addressId);
  };

  // Get all available address types for current owner (no restrictions)
  const getAvailableAddressTypes = () => {
    return ["PHYSICAL", "MAILING", "BILLING"];
  };

  // Handle bank management
  const handleAddNewBank = () => {
    if (isBankComplete()) {
      setFormData((prev) => ({
        ...prev,
        banks: [...prev.banks, { ...prev.currentBank, id: Date.now() }],
        currentBank: {
          type: "ACH",
          bankName: "",
          accountName: "",
          accountNumber: "",
          routingNumber: "",
          bankAddress: "",
          bankCity: "",
          bankState: "",
          bankCountry: "USA",
        },
      }));
      setEditingBankIndex(null);
    }
  };

  const handleEditBank = (index) => {
    // Check if there's unsaved data in currentBank
    if (hasBankData() && editingBankIndex === null) {
      // Show confirmation modal
      setConfirmAction(() => () => {
        // Load bank data into currentBank for editing
        const bankToEdit = formData.banks[index];
        setFormData((prev) => ({
          ...prev,
          currentBank: { ...bankToEdit },
        }));
        setEditingBankIndex(index);
      });
      setConfirmType("edit");
      setShowConfirmModal(true);
      return;
    }

    // Load bank data into currentBank for editing
    const bankToEdit = formData.banks[index];
    setFormData((prev) => ({
      ...prev,
      currentBank: { ...bankToEdit },
    }));
    setEditingBankIndex(index);
  };

  const handleUpdateBank = () => {
    if (isBankComplete() && editingBankIndex !== null) {
      setFormData((prev) => ({
        ...prev,
        banks: prev.banks.map((bank, idx) =>
          idx === editingBankIndex ? { ...prev.currentBank, id: bank.id } : bank
        ),
        currentBank: {
          type: "ACH",
          bankName: "",
          accountName: "",
          accountNumber: "",
          routingNumber: "",
          bankAddress: "",
          bankCity: "",
          bankState: "",
          bankCountry: "USA",
        },
      }));
      setEditingBankIndex(null);
    }
  };

  const handleCancelBankEdit = () => {
    setFormData((prev) => ({
      ...prev,
      currentBank: {
        type: "ACH",
        bankName: "",
        accountName: "",
        accountNumber: "",
        routingNumber: "",
        bankAddress: "",
        bankCity: "",
        bankState: "",
        bankCountry: "USA",
      },
    }));
    setEditingBankIndex(null);
  };

  const confirmDeleteBank = (index) => {
    setConfirmAction(() => () => {
      setFormData((prev) => ({
        ...prev,
        banks: prev.banks.filter((_, idx) => idx !== index),
      }));
      setShowConfirmModal(false);
    });
    setConfirmType("delete");
    setShowConfirmModal(true);
  };

  // Check if current owner has any data
  const hasOwnerData = () => {
    const owner = formData.currentOwner;
    return (
      owner.firstName ||
      owner.middleName ||
      owner.lastName ||
      owner.birthDate ||
      owner.citizenship ||
      owner.ownershipPercentage ||
      owner.ssn ||
      owner.contacts.length > 0 ||
      owner.addresses.length > 0
    );
  };

  // Check if current bank has any data
  const hasBankData = () => {
    const bank = formData.currentBank;
    return (
      bank.bankName ||
      bank.accountName ||
      bank.accountNumber ||
      bank.routingNumber ||
      bank.bankAddress ||
      bank.bankCity ||
      bank.bankState
    );
  };

  // Validate if current bank is complete (all required fields filled)
  const isBankComplete = () => {
    const bank = formData.currentBank;

    // Check required fields
    if (!bank.type || bank.type.trim() === "") return false;
    if (!bank.bankName || bank.bankName.trim() === "") return false;
    if (!bank.accountName || bank.accountName.trim() === "") return false;
    if (!bank.accountNumber || bank.accountNumber.trim() === "") return false;
    if (!/^\d+$/.test(bank.accountNumber)) return false;
    if (bank.accountNumber.length > 19) return false;
    if (!bank.routingNumber || bank.routingNumber.trim() === "") return false;
    if (!/^\d{9}$/.test(bank.routingNumber)) return false;
    if (!bank.bankAddress || bank.bankAddress.trim() === "") return false;
    if (!bank.bankCountry || bank.bankCountry.trim() === "") return false;

    return true;
  };

  // Validate if current owner is complete (all required fields filled)
  const isOwnerComplete = () => {
    const owner = formData.currentOwner;

    // Check required fields
    if (!owner.firstName || !owner.firstName.trim()) return false;
    if (!owner.lastName || !owner.lastName.trim()) return false;
    if (!owner.birthDate) return false;
    
    // Validate age (must be at least 18 years old)
    if (owner.birthDate) {
      const birthDateValidation = InputsValidations.validateBirthDate(owner.birthDate, 'Birth Date');
      if (!birthDateValidation.isValid) {
        return false;
      }
    }
    
    if (!owner.citizenship || !owner.citizenship.trim()) return false;
    if (!owner.ownershipPercentage || owner.ownershipPercentage === "")
      return false;

    // Check minimum 1 email and 1 phone with at least one of each being primary
    const hasEmailPrimary = owner.contacts.some(
      (c) => c.type === "Email" && c.primary === "Yes"
    );
    const hasPhonePrimary = owner.contacts.some(
      (c) => c.type === "Phone" && c.primary === "Yes"
    );
    if (!hasEmailPrimary || !hasPhonePrimary) return false;

    // Check at least 1 address
    if (owner.addresses.length === 0) return false;

    return true;
  };

  // Add current owner to owners list
  const handleAddNewOwner = () => {
    if (isOwnerComplete()) {
      setFormData((prev) => ({
        ...prev,
        owners: [...prev.owners, { ...prev.currentOwner }],
        currentOwner: {
          firstName: "",
          middleName: "",
          lastName: "",
          gender: "MALE",
          birthDate: "",
          citizenship: "",
          ownershipPercentage: "",
          ssn: "",
          contacts: [],
          addresses: [],
        },
      }));
      setEditingOwnerIndex(null);
    }
  };

  // Edit owner from list
  const handleEditOwner = (index) => {
    // Check if there's unsaved data in currentOwner
    if (hasOwnerData() && editingOwnerIndex === null) {
      // Show confirmation modal
      setConfirmAction(() => () => {
        // Load owner data into currentOwner for editing
        const ownerToEdit = formData.owners[index];
        setFormData((prev) => ({
          ...prev,
          currentOwner: { ...ownerToEdit },
        }));
        setEditingOwnerIndex(index);
      });
      setConfirmType("edit");
      setShowConfirmModal(true);
      return;
    }

    // Load owner data into currentOwner for editing
    const ownerToEdit = formData.owners[index];
    setFormData((prev) => ({
      ...prev,
      currentOwner: { ...ownerToEdit },
    }));
    setEditingOwnerIndex(index);
  };

  // Update owner in list
  const handleUpdateOwner = () => {
    if (isOwnerComplete() && editingOwnerIndex !== null) {
      setFormData((prev) => ({
        ...prev,
        owners: prev.owners.map((owner, idx) =>
          idx === editingOwnerIndex ? { ...prev.currentOwner } : owner
        ),
        currentOwner: {
          firstName: "",
          middleName: "",
          lastName: "",
          gender: "MALE",
          birthDate: "",
          citizenship: "",
          ownershipPercentage: "",
          ssn: "",
          contacts: [],
          addresses: [],
        },
      }));
      setEditingOwnerIndex(null);
    }
  };

  // Cancel owner edit
  const handleCancelOwnerEdit = () => {
    setFormData((prev) => ({
      ...prev,
      currentOwner: {
        firstName: "",
        middleName: "",
        lastName: "",
        gender: "MALE",
        birthDate: "",
        citizenship: "",
        ownershipPercentage: "",
        ssn: "",
        contacts: [],
        addresses: [],
      },
    }));
    setEditingOwnerIndex(null);
  };

  // Confirm delete owner
  const confirmDeleteOwner = (index) => {
    setConfirmAction(() => () => {
      setFormData((prev) => ({
        ...prev,
        owners: prev.owners.filter((_, i) => i !== index),
      }));
      setShowConfirmModal(false);
    });
    setConfirmType("delete");
    setShowConfirmModal(true);
  };

  // Validation functions for each step
  const isStep1Valid = () => {
    return (
      formData.requestedAmount &&
      formData.requestedAmount.trim() !== "" &&
      parseFloat(formData.requestedAmount) > 0 &&
      formData.monitoringCenter &&
      formData.monitoringCenter.trim() !== ""
    );
  };

  const isStep2Valid = () => {
    const hasAtLeastOneOwner = formData.owners.length > 0;
    const currentOwnerIsComplete = isOwnerComplete();

    if (!hasAtLeastOneOwner) {
      // Si no hay owners todavía, igual debe estar completo el que se está editando
      return currentOwnerIsComplete;
    }
  
    // Validate that all existing owners are at least 18 years old
    for (let i = 0; i < formData.owners.length; i++) {
      const owner = formData.owners[i];
      if (owner.birthDate) {
        const birthDateValidation = InputsValidations.validateBirthDate(owner.birthDate, 'Birth Date');
        if (!birthDateValidation.isValid) {
          console.error(`Validation Error: Owner ${i + 1} (${owner.firstName} ${owner.lastName}) is not at least 18 years old`);
          return false;
        }
      }
    }
  
    // Validación de ownership total
    const totalOwnership = formData.owners.reduce((sum, o) => {
      const perc = parseFloat(o.ownershipPercentage) || 0;
      return sum + perc;
    }, 0);

    if (totalOwnership > 100) {
      console.error("Validation Error: Ownership cannot exceed 100%");
      return false;
    }

    if (totalOwnership < 100) {
      console.error("Validation Error: The total ownership must equal 100%");
      return false;
    }

    // Si todo bien: al menos un owner y el currentOwner está completo
    return currentOwnerIsComplete;
  };

  const isStep3Valid = () => {
    const isValidFederalTaxId = /^\d{2}-\d{7}$/.test(
      formData.federalTaxId || ""
    );
    return (
      formData.totalSubscribers &&
      formData.totalSubscribers.trim() !== "" &&
      parseFloat(formData.totalSubscribers) > 0 &&
      formData.totalMonthlyRevenue &&
      formData.totalMonthlyRevenue.trim() !== "" &&
      parseFloat(formData.totalMonthlyRevenue) > 0 &&
      formData.legalName &&
      formData.legalName.trim() !== "" &&
      formData.dbaName &&
      formData.dbaName.trim() !== "" &&
      formData.federalTaxId &&
      formData.federalTaxId.trim() !== "" &&
      isValidFederalTaxId &&
      formData.stateOfInc &&
      formData.stateOfInc.trim() !== "" &&
      formData.businessStructure &&
      formData.businessStructure.trim() !== "" &&
      formData.dateEstablished &&
      formData.dateEstablished.trim() !== "" &&
      formData.businessPhoneNumber &&
      formData.businessPhoneNumber.trim() !== "" &&
      formData.businessAddresses.length > 0
    );
  };

  const isStep4Valid = () => {
    // Must have at least one bank saved AND currentBank must be complete (not empty, not partial)
    const hasAtLeastOneBank = formData.banks.length > 0;
    const currentBankIsComplete = isBankComplete();

    // If we have banks saved, currentBank must be completely filled (not empty)
    if (hasAtLeastOneBank) {
      return currentBankIsComplete;
    }

    // If no banks saved yet, currentBank must be complete
    return currentBankIsComplete;
  };

  // Check if we can proceed to next step
  const canProceedToNext = () => {
    switch (currentStep) {
      case STEPS.AMOUNT:
        return isStep1Valid();
      case STEPS.OWNER:
        return isStep2Valid();
      case STEPS.BUSINESS:
        return isStep3Valid();
      case STEPS.BANK:
        return isStep4Valid();
      default:
        return true;
    }
  };

  // Navigation functions
  const nextStep = () => {
    if (currentStep < STEPS.BANK) {
      // Clear any previous errors
      setError(null);
      
      // Check specific validation for current step
      if (currentStep === STEPS.OWNER) {
        // Check if we have at least one owner
        if (formData.owners.length === 0) {
          setError('Please add at least one owner before proceeding to the next step.');
          return;
        }
        
        // Validate that all owners are at least 18 years old
        for (let i = 0; i < formData.owners.length; i++) {
          const owner = formData.owners[i];
          if (owner.birthDate) {
            const birthDateValidation = InputsValidations.validateBirthDate(owner.birthDate, 'Birth Date');
            if (!birthDateValidation.isValid) {
              setError(`Owner ${i + 1} (${owner.firstName} ${owner.lastName}): ${birthDateValidation.message}`);
              return;
            }
          }
        }
        
        // Check ownership percentage
        const totalOwnership = formData.owners.reduce((sum, o) => {
          const perc = parseFloat(o.ownershipPercentage) || 0;
          return sum + perc;
        }, 0);
        
        if (totalOwnership !== 100) {
          setError('The sum of ownership percentages across all owners must equal 100%.');
          return;
        }
      }
      
      // If all validations pass, proceed to next step
      if (canProceedToNext()) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > STEPS.AMOUNT) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Execute reCAPTCHA v3 verification
      console.log("Starting reCAPTCHA verification...");

      if (!executeRecaptcha) {
        throw new Error("reCAPTCHA not available. Please try again.");
      }

      const recaptchaToken = await executeRecaptcha(
        RECAPTCHA_ACTIONS.SUBMIT_APPLICATION
      );

      // Handle case where reCAPTCHA is not available or failed
      if (!recaptchaToken || recaptchaToken === null) {
        throw new Error("reCAPTCHA verification failed. Please try again.");
      }

      if (recaptchaToken) {
        console.log("reCAPTCHA token received successfully");
      }

      // Build payload similar to the original form
      const toIsoDate = (d) => {
        if (!d) return undefined;
        if (typeof d === "string" && d.includes("-")) return d;
        return undefined;
      };

      const addressDtos = [];

      // Add business addresses
      formData.businessAddresses.forEach((address) => {
        addressDtos.push({
          addressType: address.addressType || "PHYSICAL",
          street1: address.streetAddress,
          street2: address.streetAddress2 || null,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
          country: address.country || "USA",
          phoneNumber: address.phoneNumber || null,
        });
      });

      const ownerDtos = [];

      // Helper function to check if owner has any meaningful data
      const hasAnyOwnerData = (owner) => {
        return (
          owner.firstName ||
          owner.middleName ||
          owner.lastName ||
          owner.birthDate ||
          owner.citizenship ||
          owner.ownershipPercentage ||
          owner.ssn ||
          owner.contacts.length > 0 ||
          owner.addresses.length > 0
        );
      };

      // Add all owners from the saved owners array
      formData.owners.forEach((owner) => {
        if (hasAnyOwnerData(owner)) {
          ownerDtos.push({
            firstName: owner.firstName,
            middleName: owner.middleName,
            lastName: owner.lastName,
            gender: (owner.gender || "OTHER").toString().toUpperCase(),
            birthDate: owner.birthDate,
            citizenship: owner.citizenship,
            ownershipPercentage: parseFloat(owner.ownershipPercentage) || 0,
            socialSecurityNumber: owner.ssn,
            contacts: owner.contacts.map((c) => ({
              type: c.type === "Phone" ? "PHONE" : "EMAIL",
              value: c.value,
              isPrimary: c.primary === "Yes",
            })),
            addresses: owner.addresses.map((a) => ({
              addressType: a.addressType || "PHYSICAL",
              street1: a.streetAddress,
              street2: a.streetAddress2 || null,
              city: a.city,
              state: a.state,
              zipCode: a.zipCode,
              country: a.country || "USA",
              phoneNumber: a.phoneNumber || null,
            })),
          });
        }
      });

      // Also add the current owner if it has data (not yet saved to owners array)
      if (hasAnyOwnerData(formData.currentOwner)) {
        ownerDtos.push({
          firstName: formData.currentOwner.firstName,
          middleName: formData.currentOwner.middleName,
          lastName: formData.currentOwner.lastName,
          gender: (formData.currentOwner.gender || "OTHER")
            .toString()
            .toUpperCase(),
          birthDate: formData.currentOwner.birthDate,
          citizenship: formData.currentOwner.citizenship,
          ownershipPercentage:
            parseFloat(formData.currentOwner.ownershipPercentage) || 0,
          socialSecurityNumber: formData.currentOwner.ssn,
          contacts: formData.currentOwner.contacts.map((c) => ({
            type: c.type === "Phone" ? "PHONE" : "EMAIL",
            value: c.value,
            isPrimary: c.primary === "Yes",
          })),
          addresses: formData.currentOwner.addresses.map((a) => ({
            addressType: a.addressType || "PHYSICAL",
            street1: a.streetAddress,
            street2: a.streetAddress2 || null,
            city: a.city,
            state: a.state,
            zipCode: a.zipCode,
            country: a.country || "USA",
            phoneNumber: a.phoneNumber || null,
          })),
        });
      }

      // Build banks array - include both saved banks AND currentBank if it has data
      const bankDtos = [];

      // Add saved banks
      formData.banks.forEach((bank) => {
        bankDtos.push({
          type: bank.type || "ACH",
          bankName: bank.bankName,
          accountName: bank.accountName,
          number: bank.accountNumber,
          routingNumber: bank.routingNumber,
          bankAddress: bank.bankAddress,
          bankCity: bank.bankCity || null,
          bankState: bank.bankState || null,
          bankCountry: bank.bankCountry || "USA",
        });
      });

      // Add currentBank if it has data (user filled form but didn't click "Add Bank")
      const currentBank = formData.currentBank;
      if (
        currentBank.bankName ||
        currentBank.accountName ||
        currentBank.accountNumber ||
        currentBank.routingNumber
      ) {
        bankDtos.push({
          type: currentBank.type || "ACH",
          bankName: currentBank.bankName,
          accountName: currentBank.accountName,
          number: currentBank.accountNumber,
          routingNumber: currentBank.routingNumber,
          bankAddress: currentBank.bankAddress,
          bankCity: currentBank.bankCity || null,
          bankState: currentBank.bankState || null,
          bankCountry: currentBank.bankCountry || "USA",
        });
      }

      const payload = {
        amount: parseFloat(formData.requestedAmount) || 0,
        totalSubscribers: parseInt(formData.totalSubscribers) || 0,
        totalMonthlyRevenue: parseFloat(formData.totalMonthlyRevenue) || 0,
        monitoringCenter: formData.monitoringCenter,
        monitoringCenterId: formData.monitoringCenterId,
        merchantPayload: {
          type: "DEALER",
          legalName: formData.legalName,
          dbaName: formData.dbaName,
          federalTaxId: formData.federalTaxId || null,
          stateOfInc: formData.stateOfInc || null,
          industry: "Alarms",
          businessStructure: formData.businessStructure || null,
          website: formData.website || undefined,
          dateEstablished: toIsoDate(formData.dateEstablished),
          phoneNumber: formData.businessPhoneNumber || null,
          addresses: addressDtos,
          owners: ownerDtos,
          banks: bankDtos,
        },
        merchantId: null,
      };

      // Use external application controller for creating application
      const response = await applicationExternalController.createApplication(
        payload
      );

      // Store application ID for confirmation page (external controller returns axios response)
      const applicationData = response?.data || response;
      const applicationId = applicationData?.id || "N/A";
      const dealPrivateId =
        applicationData?.privateId || applicationData?.id || null;
      console.log("✅ Application created with ID:", applicationId);
      console.log("✅ Deal privateId:", dealPrivateId);

      // Get first owner's email for notification (use ownerDtos, not formData.owners)
      const firstOwner = ownerDtos.length > 0 ? ownerDtos[0] : null;
      const primaryEmail =
        firstOwner?.contacts?.find((c) => c.type === "EMAIL")?.value || null;
      const ownerName = firstOwner
        ? `${firstOwner.firstName} ${firstOwner.lastName}`.trim()
        : "Valued Customer";

      console.log("📧 Owner email for notification:", primaryEmail);
      console.log("👤 Owner name:", ownerName);

      // Send application created email BEFORE navigating
      if (primaryEmail && applicationId && dealPrivateId) {
        try {
          console.log("📧 Sending application created email...");

          // Get the application created email template from external service
          const templateResponse =
            await templateExternalController.getApplicationCreatedEmailTemplate();
          const templates = templateResponse.data;

          if (templates && Array.isArray(templates) && templates.length > 0) {
            const template = templates[0];
            console.log("✅ Template obtained from server:", template);

            // Process template with actual values
            const processedTemplate = {
              id: String(template.id).trim(),
              name: String(template.name).trim(),
              channel: String(template.channel).trim(),
              templateType: String(template.templateType).trim(),
              subject: String(template.subject).trim(),
              body: String(template.body).trim(),
              values: {
                company_logo_url:
                  "https://via.placeholder.com/120x60/0b5fff/ffffff?text=NLF",
                company_name: "Next Level Funding",
                user_name: String(ownerName).trim(),
                application_id: String(applicationId).trim(),
                support_email: "support@nlf.com",
                support_phone: "1-800-NLF-HELP",
              },
            };

            // Generate unique request ID
            const emailRequestId = `application-created-email-${Date.now()}-${applicationId}`;

            // Prepare communication payload
            const communicationPayload = {
              requestId: String(emailRequestId).trim(),
              action: "OTHER",
              dealId: dealPrivateId,
              contacts: [
                {
                  id: `application-${applicationId}`,
                  name: String(ownerName).trim(),
                  email: String(primaryEmail).trim(),
                  channels: ["EMAIL"],
                },
              ],
              templates: [processedTemplate],
            };

            console.log(
              "📧 Communication payload:",
              JSON.stringify(communicationPayload, null, 2)
            );

            // Send Email via external communication service
            await communicationExternalController.dispatch(
              communicationPayload
            );
            console.log(
              "✅ Application created email sent successfully to",
              primaryEmail
            );
          } else {
            console.error("❌ No template found");
          }
        } catch (emailError) {
          console.error(
            "❌ Failed to send application created email:",
            emailError
          );
          console.error("❌ Email error details:", emailError.response?.data);
          // Don't throw error to avoid interrupting the navigation
        }
      }

      // Navigate to confirmation page with application ID and owner info
      navigate("/external/application/confirmation", {
        state: {
          applicationId,
          ownerEmail: primaryEmail,
          ownerName: ownerName,
        },
      });
    } catch (error) {
      console.error("Error submitting application:", error);
      setError(
        error.message ||
          "An error occurred while submitting the application. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.AMOUNT:
        return (
          <ExternalAmountStep
            formData={formData}
            handleInputChange={handleInputChange}
            monitoringCenterOptions={monitoringCenterOptions}
            setFormData={setFormData}
          />
        );

      case STEPS.OWNER:
        return (
          <ExternalOwnerInformation
            formData={formData}
            handleInputChange={handleInputChange}
            editingOwnerIndex={editingOwnerIndex}
            handleAddNewOwner={handleAddNewOwner}
            handleUpdateOwner={handleUpdateOwner}
            handleCancelOwnerEdit={handleCancelOwnerEdit}
            handleEditOwner={handleEditOwner}
            confirmDeleteOwner={confirmDeleteOwner}
            setShowContactModal={setShowContactModal}
            setContactToEdit={setContactToEdit}
            handleEditContact={handleEditContact}
            handleDeleteContact={handleDeleteContact}
            setShowAddressModal={setShowAddressModal}
            setAddressToEdit={setAddressToEdit}
            handleEditAddress={handleEditAddress}
            handleDeleteAddress={handleDeleteAddress}
            isOwnerComplete={isOwnerComplete}
          />
        );

      case STEPS.BUSINESS:
        return (
          <ExternalBusinessInformation
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            validateWebsiteField={validateWebsiteField}
            setErrors={setErrors}
            setShowBusinessAddressModal={setShowBusinessAddressModal}
            setBusinessAddressToEdit={setBusinessAddressToEdit}
            handleEditBusinessAddress={handleEditBusinessAddress}
            handleDeleteBusinessAddress={handleDeleteBusinessAddress}
          />
        );

      case STEPS.BANK:
        return (
          <div>
            {/* Show list of added banks */}
            {formData.banks.length > 0 && (
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="mb-0">
                    <i className="ni ni-building mr-2" />
                    Added Bank Accounts ({formData.banks.length})
                  </h5>
                </CardHeader>
                <CardBody>
                  {formData.banks.map((bank, index) => (
                    <div key={bank.id} className="border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1">
                            <span className="badge badge-primary mr-2">
                              {bank.type}
                            </span>
                            {bank.bankName}
                          </h6>
                          <p className="mb-1 text-sm">
                            <strong>Account Name:</strong> {bank.accountName}
                          </p>
                          <p className="mb-0 text-sm">
                            <strong>Account #:</strong> ****
                            {bank.accountNumber?.slice(-4) || ""}
                          </p>
                        </div>
                        <div>
                          <Button
                            color="warning"
                            size="sm"
                            className="mr-2"
                            onClick={() => handleEditBank(index)}
                            disabled={editingBankIndex === index}
                          >
                            <i className="ni ni-settings-gear-65" />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => confirmDeleteBank(index)}
                          >
                            <i className="ni ni-fat-remove" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}

            <Card className="mb-4">
              <CardHeader>
                <h5 className="mb-0">
                  <i className="ni ni-building mr-2" />
                  {editingBankIndex !== null
                    ? `Editing Bank Account #${editingBankIndex + 1}`
                    : formData.banks.length > 0
                    ? "Add Another Bank Account"
                    : "Bank Information"}
                </h5>
                <p className="text-sm text-muted mb-0">
                  At least one bank account required
                </p>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="bankType">Bank Type *</Label>
                      <Input
                        id="bankType"
                        type="select"
                        value={formData.currentBank.type}
                        onChange={(e) =>
                          handleInputChange("type", e.target.value, true)
                        }
                      >
                        <option value="ACH">ACH</option>
                        <option value="WIREBANK">Wirebank</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="bankName">Bank Name *</Label>
                      <Input
                        id="bankName"
                        type="text"
                        value={formData.currentBank.bankName}
                        onChange={(e) =>
                          handleInputChange("bankName", e.target.value, true)
                        }
                        placeholder="Enter bank name"
                        maxLength={255}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="accountName">Account Name *</Label>
                      <Input
                        id="accountName"
                        type="text"
                        value={formData.currentBank.accountName}
                        onChange={(e) =>
                          handleInputChange("accountName", e.target.value, true)
                        }
                        placeholder="Enter account name"
                        maxLength={255}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="accountNumber">Account Number *</Label>
                      <Input
                        id="accountNumber"
                        type="text"
                        value={formData.currentBank.accountNumber}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 19);
                          handleInputChange("accountNumber", value, true);
                        }}
                        placeholder="Enter account number"
                        maxLength={19}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="routingNumber">Routing Number *</Label>
                      <Input
                        id="routingNumber"
                        type="text"
                        value={formData.currentBank.routingNumber}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 9);
                          handleInputChange("routingNumber", value, true);
                        }}
                        placeholder="9 digits"
                        maxLength={9}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label htmlFor="bankCity">Bank City *</Label>
                      <Input
                        id="bankCity"
                        type="text"
                        value={formData.currentBank.bankCity}
                        onChange={(e) =>
                          handleInputChange("bankCity", e.target.value, true)
                        }
                        placeholder="Enter bank city"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label htmlFor="bankAddress">Bank Address *</Label>
                  <ExtAddressAutocomplete
                    value={formData.currentBank.bankAddress}
                    onChange={(text) =>
                      handleInputChange("bankAddress", text, true)
                    }
                    onSelect={(item) => {
                      const label = item?.label || "";
                      const details = item?.details || {};
                      handleInputChange("bankAddress", label, true);
                      handleInputChange("bankCity", details.city || "", true);
                      handleInputChange("bankState", details.state || "", true);
                      handleInputChange(
                        "bankCountry",
                        details.country || "USA",
                        true
                      );
                    }}
                    placeholder="Start typing bank address..."
                  />
                </FormGroup>
              </CardBody>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  // Get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case STEPS.AMOUNT:
        return "Business Metrics & Monitoring Center";
      case STEPS.OWNER:
        return "Owner Information";
      case STEPS.BUSINESS:
        return "Business Information";
      case STEPS.BANK:
        return "Bank Information";
      default:
        return "";
    }
  };

  // Calculate progress
  const progress = ((currentStep + 1) / 4) * 100;

  return (
    <Container className="mt-4" fluid>
      <Row className="justify-content-center">
        <Col lg="10" xl="8">
          <Card className="shadow">
            <CardHeader className="bg-gradient-primary text-white">
              <h2 className="text-white mb-0">
                <i className="ni ni-single-copy-04 mr-2" />
                Credit Application Form
              </h2>
              <p className="text-white mb-0 mt-2" style={{ opacity: 0.9 }}>
                Complete the form below to apply for credit. Fields marked with
                * are required.
              </p>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-4">
                  <i className="ni ni-bell-55 mr-2" />
                  {error}
                </Alert>
              )}

              {success && (
                <Alert color="success" className="mb-4">
                  <i className="ni ni-check-bold mr-2" />
                  {success}
                </Alert>
              )}

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">{getStepTitle()}</h5>
                  <span className="text-muted">
                    Step {currentStep + 1} of 4
                  </span>
                </div>
                <Progress value={progress} className="mb-3" />
              </div>

              <Form>
                {renderStepContent()}

                {/* Navigation Buttons */}
                <Row className="mt-4">
                  <Col className="text-center">
                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={prevStep}
                        disabled={currentStep === STEPS.AMOUNT}
                        size="sm"
                      >
                        <i className="ni ni-bold-left mr-2" />
                        Previous
                      </Button>

                      {/* Center action buttons for Owner and Bank steps */}
                      <div>
                        {currentStep === STEPS.OWNER && (
                          <>
                            {editingOwnerIndex !== null ? (
                              <>
                                <Button
                                  type="button"
                                  color="success"
                                  onClick={handleUpdateOwner}
                                  disabled={!isOwnerComplete()}
                                  className="mr-2"
                                  size="sm"
                                >
                                  <i className="ni ni-check-bold mr-2" />
                                  Update Owner
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleCancelOwnerEdit}
                                  size="sm"
                                >
                                  <i className="ni ni-fat-remove mr-2" />
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="button"
                                color="success"
                                onClick={handleAddNewOwner}
                                disabled={!isOwnerComplete()}
                                size="sm"
                              >
                                <i className="ni ni-fat-add mr-2" />
                                {formData.owners.length > 0
                                  ? "Add Another Owner"
                                  : "Add Owner"}
                              </Button>
                            )}
                          </>
                        )}

                        {currentStep === STEPS.BANK && (
                          <>
                            {editingBankIndex !== null ? (
                              <>
                                <Button
                                  type="button"
                                  color="success"
                                  onClick={handleUpdateBank}
                                  disabled={!isBankComplete()}
                                  className="mr-2"
                                  size="sm"
                                >
                                  <i className="ni ni-check-bold mr-2" />
                                  Update Bank
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleCancelBankEdit}
                                  size="sm"
                                >
                                  <i className="ni ni-fat-remove mr-2" />
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="button"
                                color="success"
                                onClick={handleAddNewBank}
                                disabled={
                                  !isBankComplete() ||
                                  (formData.banks.length > 0 && !hasBankData())
                                }
                                size="sm"
                              >
                                <i className="ni ni-fat-add mr-2" />
                                {formData.banks.length > 0
                                  ? "Add Another Bank"
                                  : "Add Bank"}
                              </Button>
                            )}
                          </>
                        )}
                      </div>

                      <div>
                        {currentStep === STEPS.BANK ? (
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            disabled={isSubmitting || !canProceedToNext()}
                            onClick={handleSubmit}
                          >
                            {isSubmitting ? (
                              <>
                                <Spinner size="sm" className="mr-2" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <i className="ni ni-check-bold mr-2" />
                                Submit Application
                              </>
                            )}
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            color="primary"
                            onClick={nextStep}
                            disabled={!canProceedToNext()}
                            size="sm"
                          >
                            Next
                            <i className="ni ni-bold-right ml-2" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modals */}
      <ContactModal
        isOpen={showContactModal}
        toggle={() => {
          setShowContactModal(false);
          setContactToEdit(null);
        }}
        onSave={contactToEdit ? handleUpdateContact : handleAddContact}
        contactToEdit={contactToEdit}
        existingContacts={formData.currentOwner.contacts}
      />

      <AddressModal
        isOpen={showAddressModal}
        toggle={() => {
          setShowAddressModal(false);
          setAddressToEdit(null);
        }}
        onSave={addressToEdit ? handleUpdateAddress : handleAddAddress}
        addressToEdit={addressToEdit}
        getAvailableAddressTypes={getAvailableAddressTypes}
      />

      <BusinessAddressModal
        isOpen={showBusinessAddressModal}
        toggle={() => {
          setShowBusinessAddressModal(false);
          setBusinessAddressToEdit(null);
        }}
        onSave={
          businessAddressToEdit
            ? handleUpdateBusinessAddress
            : handleAddBusinessAddress
        }
        addressToEdit={businessAddressToEdit}
      />

      <ConfirmationModal
        isOpen={showConfirmModal}
        toggle={() => setShowConfirmModal(false)}
        title={confirmType === "delete" ? "Confirm Deletion" : "Confirm Action"}
        message={
          confirmType === "delete"
            ? "Are you sure you want to delete this item? This action cannot be undone."
            : "You have unsaved data in the current form. If you proceed, this data will be lost. Do you want to continue?"
        }
        onConfirm={confirmAction}
        confirmText={confirmType === "delete" ? "Delete" : "Yes, Continue"}
        confirmColor={confirmType === "delete" ? "danger" : "warning"}
        icon={
          confirmType === "delete"
            ? "ni ni-fat-remove"
            : "ni ni-notification-70"
        }
      />
    </Container>
  );
};

export default ExternalApplicationWizard;
